import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabaseClient";
import "./Comment.css";

// 댓글 목록 컴포넌트
const CommentList = ({ itemId, itemAuthorId }) => {
  // 댓글 목록
  const [comments, setComments] = useState([]);

  // 현재 로그인한 사용자 id
  const [currentUserId, setCurrentUserId] = useState(null);

  // 관리자 여부
  const [isAdmin, setIsAdmin] = useState(false);

  // 새 댓글 입력값
  const [newComment, setNewComment] = useState("");

  // 새 댓글 비밀 여부
  const [isSecret, setIsSecret] = useState(false);

  // 답글 작성 중인 댓글 id
  const [replyingTo, setReplyingTo] = useState(null);

  // 답글 입력값
  const [replyContent, setReplyContent] = useState("");

  // 답글 비밀 여부
  const [isReplySecret, setIsReplySecret] = useState(false);

  // 댓글 목록 조회
  const fetchComments = useCallback(async () => {
    try {
      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) throw authError;

      let myId = null;

      if (authUser) {
        // users 테이블에서 사용자 id와 역할 조회
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("id, role")
          .eq("email", authUser.email)
          .single();

        if (userError) throw userError;

        myId = userData.id;
        setIsAdmin(userData.role === "admin");
      }

      setCurrentUserId(myId);

      // 댓글 목록 조회
      const { data, error } = await supabase
        .from("comments")
        .select("*")
        .eq("lost_item_id", itemId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // 화면 표시용 데이터 가공
      const formattedComments = (data || []).map((comment) => ({
        id: comment.id,
        authorId: comment.user_id,
        authorName:
          comment.user_id === myId
            ? "나"
            : comment.user_id === itemAuthorId
            ? "글쓴이"
            : "익명",
        text: comment.content,
        date: new Date(
          comment.created_at.endsWith("Z")
            ? comment.created_at
            : `${comment.created_at}Z`
        ).toLocaleString("ko-KR", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Seoul",
        }),
        isSecret: comment.is_secret,
        parentId: comment.parent_id,
      }));

      setComments(formattedComments);
    } catch (error) {
      console.error("댓글 불러오기 에러:", error.message);
    }
  }, [itemId, itemAuthorId]);

  // itemId 변경 시 댓글 다시 조회
  useEffect(() => {
    if (itemId) {
      fetchComments();
    }
  }, [itemId, fetchComments]);

  // 댓글 또는 답글 등록
  const handleAddComment = async (e, parentId = null) => {
    e.preventDefault();

    const contentToSave = parentId ? replyContent : newComment;
    const secretToSave = parentId ? isReplySecret : isSecret;

    if (!contentToSave.trim()) {
      alert("내용을 입력해주세요!");
      return;
    }

    if (!currentUserId) {
      alert("로그인을 먼저 해주세요!");
      return;
    }

    try {
      const { error } = await supabase.from("comments").insert([
        {
          lost_item_id: itemId,
          user_id: currentUserId,
          content: contentToSave,
          is_secret: secretToSave,
          parent_id: parentId,
        },
      ]);

      if (error) throw error;

      // 답글 작성 상태 초기화
      if (parentId) {
        setReplyContent("");
        setIsReplySecret(false);
        setReplyingTo(null);
      } else {
        // 댓글 작성 상태 초기화
        setNewComment("");
        setIsSecret(false);
      }

      fetchComments();
    } catch (error) {
      console.error("댓글 저장 에러:", error.message);
      alert("댓글 등록에 실패했습니다: " + error.message);
    }
  };

  // 댓글 및 답글 삭제
  const handleDelete = async (commentId, isReply = false) => {
    const confirmMessage = isReply
      ? "정말 이 답글을 삭제하시겠습니까?"
      : "정말 이 댓글을 삭제하시겠습니까? (달려있는 답글도 함께 삭제됩니다)";

    const isConfirm = window.confirm(confirmMessage);
    if (!isConfirm) return;

    const { error } = await supabase
      .from("comments")
      .delete()
      .or(`id.eq.${commentId},parent_id.eq.${commentId}`);

    if (error) {
      console.error("댓글 삭제 에러:", error.message);
      alert("댓글 삭제에 실패했습니다.");
      return;
    }

    fetchComments();
  };

  // 부모 댓글 목록
  const parentComments = comments.filter((c) => !c.parentId);

  // 특정 댓글의 답글 조회
  const getReplies = (parentId) =>
    comments.filter((c) => c.parentId === parentId);

  // 비밀 댓글 열람 가능 여부 확인
  const canViewSecret = (comment) => {
    if (!comment.isSecret) return true;

    if (currentUserId === comment.authorId || currentUserId === itemAuthorId) {
      return true;
    }

    if (comment.parentId) {
      const parentComment = comments.find((c) => c.id === comment.parentId);

      if (parentComment && currentUserId === parentComment.authorId) {
        return true;
      }
    }

    return false;
  };

  // 삭제 가능 여부 확인 (작성자 또는 관리자)
  const canDelete = (comment) => {
    return currentUserId === comment.authorId || isAdmin;
  };

  return (
    <div className="comment-section">
      <hr className="comment-divider" />
      <h3 className="comment-title">댓글 {comments.length}개</h3>

      {/* 댓글 입력 폼 */}
      {currentUserId && (
        <form
          className="comment-input-form"
          onSubmit={(e) => handleAddComment(e, null)}
        >
          <div className="input-row">
            <input
              type="text"
              className="comment-input-box"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="댓글을 남겨보세요..."
            />

            <button type="submit" className="comment-submit-btn">
              등록
            </button>
          </div>

          <label className="secret-checkbox">
            <input
              type="checkbox"
              checked={isSecret}
              onChange={(e) => setIsSecret(e.target.checked)}
            />
            🔒 비밀 댓글
          </label>
        </form>
      )}

      {/* 댓글 목록 */}
      <div className="comment-list">
        {parentComments.map((comment) => (
          <div key={comment.id} className="comment-wrapper">

            {/* 댓글 */}
            <div className={`comment-item ${comment.isSecret ? "secret" : ""}`}>
              <div className="comment-header">
                <span
                  className={`comment-author ${
                    comment.authorName === "나" ||
                    comment.authorName === "글쓴이"
                      ? "highlight"
                      : ""
                  }`}
                >
                  {comment.authorName} {comment.isSecret && "🔒"}
                </span>

                <span className="comment-date">{comment.date}</span>
              </div>

              <p className="comment-content">
                {canViewSecret(comment)
                  ? comment.text
                  : "🔒 비밀 댓글입니다."}
              </p>

              <div className="comment-actions">
                {/* 답글 버튼 */}
                <button
                  type="button"
                  className="action-btn"
                  onClick={() =>
                    setReplyingTo(replyingTo === comment.id ? null : comment.id)
                  }
                >
                  {replyingTo === comment.id ? "답글 취소" : "답글 달기"}
                </button>

                {/* 삭제 버튼 */}
                {canDelete(comment) && (
                  <button
                    type="button"
                    className="action-btn delete"
                    onClick={() => handleDelete(comment.id)}
                  >
                    삭제
                  </button>
                )}
              </div>
            </div>

            {/* 답글 입력 폼 */}
            {replyingTo === comment.id && (
              <form
                className="reply-input-form"
                onSubmit={(e) => handleAddComment(e, comment.id)}
              >
                <span className="reply-arrow">↳</span>

                <div className="reply-input-container">
                  <div className="input-row">
                    <input
                      type="text"
                      className="reply-input-box"
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="답글을 남겨보세요..."
                      autoFocus
                    />

                    <button type="submit" className="reply-submit-btn">
                      답글 등록
                    </button>
                  </div>

                  <label className="secret-checkbox reply-secret">
                    <input
                      type="checkbox"
                      checked={isReplySecret}
                      onChange={(e) => setIsReplySecret(e.target.checked)}
                    />
                    🔒 비밀 답글
                  </label>
                </div>
              </form>
            )}

            {/* 답글 목록 */}
            <div className="replies-container">
              {getReplies(comment.id).map((reply) => (
                <div
                  key={reply.id}
                  className={`comment-item reply-item ${
                    reply.isSecret ? "secret" : ""
                  }`}
                >
                  <div className="comment-header">
                    <span className="reply-arrow">↳</span>

                    <span
                      className={`comment-author ${
                        reply.authorName === "나" ||
                        reply.authorName === "글쓴이"
                          ? "highlight"
                          : ""
                      }`}
                    >
                      {reply.authorName} {reply.isSecret && "🔒"}
                    </span>

                    <span className="comment-date">{reply.date}</span>
                  </div>

                  <p className="comment-content reply-content">
                    {canViewSecret(reply)
                      ? reply.text
                      : "🔒 비밀 답글입니다."}
                  </p>

                  {/* 답글 삭제 버튼 */}
                  {canDelete(reply) && (
                    <div className="comment-actions">
                      <button
                        type="button"
                        className="action-btn delete"
                        onClick={() => handleDelete(reply.id, true)}
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        ))}
      </div>
    </div>
  );
};

export default CommentList;